import React, { useEffect, useState } from "react";
import styles from "../Styles/registerContainer.module.css";
import { lang } from "../Utils/lang";
import { Button, Grid } from "@mui/material";
import Stepper from "@mui/material/Stepper";
import Step from "@mui/material/Step";
import StepButton from "@mui/material/StepButton";
import {
  UserDataModel,
  DelegatesModel,
  DocumentsModel,
  CompanyDataModel,
  LocationModel,
} from "../Models/RegisterModel";
import { yupResolver } from "@hookform/resolvers/yup";
import { useForm } from "react-hook-form";
import UserData from "../Components/Register/UserData";
import CompanyData from "../Components/Register/CompanyData";
import Delegate from "../Components/Register/Delegate";
import Documents from "../Components/Register/Documents";
import { useWebSocket } from "../Context/WebSocketContext";
import { urlEnum, allowedReferrerOrigins } from "../Utils/urlEnum";
import Loading from "../Components/Reusable/Loading";
import AdministrativeLocationData from "../Components/Register/AdministrativeLocationData";

export default function Register() {
  const [activeStep, setActiveStep] = useState(0);
  const [creatingSetup, setCreatingSetup] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("");
  const [accessDenied, setAccessDenied] = useState(false);
  const { stompClient } = useWebSocket();

  useEffect(() => {
    const referrer = document.referrer;
    const allowed = allowedReferrerOrigins.some((origin) =>
      referrer.startsWith(origin)
    );
    if (!allowed) {
      setAccessDenied(true);
    }
  }, []);

  const userDataModel = useForm({
    resolver: yupResolver(UserDataModel),
  });

  const companyDataModel = useForm({
    resolver: yupResolver(CompanyDataModel),
  });

  const delegatesModel = useForm({
    resolver: yupResolver(DelegatesModel),
  });

  const documentsModel = useForm({
    resolver: yupResolver(DocumentsModel),
  });

  const locationModel = useForm({
    resolver: yupResolver(LocationModel),
  });

  /**
   * Subscribe to private message (receive messages from other users in the current chat)
   */
  useEffect(() => {
    if (!stompClient) return;
    const subscription = stompClient.subscribe(urlEnum.registerStatus, onPrivateMessage);
    //const subscription = stompClient.subscribe("/topic/organization", onPrivateMessage);
    return () => {
      subscription.unsubscribe();
    };
  }, [stompClient]);

  const onPrivateMessage = (payload: any) => {
    const payloadData = JSON.parse(payload.body);
    setCurrentStatus(payloadData.currentStatus);
    if (payloadData.currentStatus === "FINAL") {
      setCurrentStatus("Se creeaza contul administratorului.");
      setTimeout(() => {
        setCreatingSetup(false);
        const companySlug = String(payloadData.companyName)
          .trim()
          .replace(/\s+/g, "")
          .replace(/[^a-zA-Z0-9-]/g, "");
        window.location.href = `https://${companySlug}.erp.xauto.ro/`;
      }, 5000);
    }
  };

  const steps = [
    lang.userData,
    lang.companyData,
    lang.location,
    lang.delegate,
    lang.documents,
  ];

  const handleNext = () => {
    const newActiveStep = activeStep + 1;
    setActiveStep(newActiveStep);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  /**
   * Handles the submission of the registration form.
   *
   * @param {any} event - The event object representing the form submission.
   * @return {void} This function does not return a value.
   */
  const handleRegisterSubmit = async (allFormsData: any) => {
    const chatMessage = {
      senderName: urlEnum.senderName,
      receiverName: urlEnum.senderName,
      message: allFormsData,
      status: "MESSAGE",
    };
    setCreatingSetup(true);
    stompClient?.send(urlEnum.register, {}, JSON.stringify(chatMessage));
  };

  const handleStep = (step: number) => () => {
    setActiveStep(step);
  };

  const handleUserDataSubmit = () => {
    // eslint-disable-next-line no-console
    handleNext();
  };

  const onErrorUserData = (data: any) => {
    // eslint-disable-next-line no-console
    console.log(data); //E lasat aici pentru a vedeam campurile care nu sunt completate
  };

  /**
   * This is the final submit that will be called when the form is submitted.
   * @param data
   */
  const handleDocumentsDataSubmit = (data: any) => {
    // eslint-disable-next-line no-console

    const userData = userDataModel.getValues();
    const allFormsData = {
      ...data,
      ...userData,
      originalCompanyName: userData.companyName,
      companyName: String(userData.companyName)
        .trim()
        .replace(/\s+/g, "")
        .replace(/[^a-zA-Z0-9-]/g, ""),
      ...companyDataModel.getValues(),
      ...delegatesModel.getValues(),
      ...{ location: locationModel.getValues() },
    };
    handleRegisterSubmit(allFormsData);
  };

  const onErrorDocumentsData = (data: any) => {
    // eslint-disable-next-line no-console
    console.log(data); //E lasat aici pentru a vedeam campurile care nu sunt completate
  };

  if (accessDenied) {
    return (
      <Grid
        container
        justifyContent="center"
        alignItems="center"
        sx={{ minHeight: "100vh", flexDirection: "column", gap: 2 }}
      >
        <h2>{lang.accessDenied}</h2>
        <p>{lang.accessDeniedDescription}</p>
      </Grid>
    );
  }

  return (
    <div className={styles.registerContainer}>
      {creatingSetup && <Loading message={currentStatus} />}
      <div className={styles.imageContainer}>
        <img src="/ccf2.png" alt="Register Img" />
      </div>
      <div className={styles.formContainer}>
        <Grid
          container
          direction="column"
          justifyContent="space-between"
          alignItems="center"
          sx={{ minHeight: "100vh" }}
        >
          <h1>{lang.registerAccount}</h1>

          <Grid item sx={{ width: "100%", mt: 4 }}>
            <Stepper activeStep={activeStep}>
              {steps.map((label, index) => (
                <Step key={label}>
                  <StepButton color="inherit" onClick={handleStep(index)}>
                    {label}
                  </StepButton>
                </Step>
              ))}
            </Stepper>
          </Grid>
          <Grid item sx={{ flex: 1, mt: 8, mb: 2, width: "70%" }}>
            {activeStep === 0 && (
              <form
                onSubmit={userDataModel.handleSubmit(
                  handleUserDataSubmit,
                  onErrorUserData,
                )}
                method="get"
                id="checkUserData"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <UserData control={userDataModel.control} />
                <Grid item sx={{ width: "100%" }}>
                  <Grid
                    container
                    justifyContent="space-between"
                    sx={{ padding: 2 }}
                  >
                    <Button
                      onClick={handleBack}
                      variant="contained"
                      type="button"
                      disabled={0 === activeStep}
                    >
                      {lang.back}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={steps.length - 1 === activeStep}
                    >
                      {lang.next}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}

            {activeStep === 1 && (
              <form
                onSubmit={companyDataModel.handleSubmit(
                  handleUserDataSubmit,
                  onErrorUserData,
                )}
                method="get"
                id="checkUserData"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <CompanyData control={companyDataModel.control} />
                <Grid item sx={{ width: "100%" }}>
                  <Grid
                    container
                    justifyContent="space-between"
                    sx={{ padding: 2 }}
                  >
                    <Button
                      onClick={handleBack}
                      variant="contained"
                      type="button"
                    >
                      {lang.back}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={steps.length - 1 === activeStep}
                    >
                      {lang.next}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}

            {activeStep === 2 && (
              <form
                onSubmit={locationModel.handleSubmit(
                  handleUserDataSubmit,
                  onErrorUserData,
                )}
                method="get"
                id="checkUserData"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <AdministrativeLocationData control={locationModel.control} />
                <Grid item sx={{ width: "100%" }}>
                  <Grid
                    container
                    justifyContent="space-between"
                    sx={{ padding: 2 }}
                  >
                    <Button
                      onClick={handleBack}
                      variant="contained"
                      type="button"
                    >
                      {lang.back}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={steps.length - 1 === activeStep}
                    >
                      {lang.next}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
            {activeStep === 3 && (
              <form
                onSubmit={delegatesModel.handleSubmit(
                  handleUserDataSubmit,
                  onErrorUserData,
                )}
                method="get"
                id="checkUserData"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Delegate control={delegatesModel.control} />
                <Grid item sx={{ width: "100%" }}>
                  <Grid
                    container
                    justifyContent="space-between"
                    sx={{ padding: 2 }}
                  >
                    <Button
                      onClick={handleBack}
                      variant="contained"
                      type="button"
                    >
                      {lang.back}
                    </Button>
                    <Button
                      variant="contained"
                      type="submit"
                      disabled={steps.length - 1 === activeStep}
                    >
                      {lang.next}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
            {activeStep === 4 && (
              <form
                onSubmit={documentsModel.handleSubmit(
                  handleDocumentsDataSubmit,
                  onErrorDocumentsData,
                )}
                method="get"
                id="checkUserData"
                style={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "20px",
                }}
              >
                <Documents control={documentsModel.control} />
                <Grid item sx={{ width: "100%" }}>
                  <Grid
                    container
                    justifyContent="space-between"
                    sx={{ padding: 2 }}
                  >
                    <Button
                      onClick={handleBack}
                      variant="contained"
                      type="button"
                    >
                      {lang.back}
                    </Button>
                    <Button variant="contained" type="submit">
                      {lang.register}
                    </Button>
                  </Grid>
                </Grid>
              </form>
            )}
          </Grid>
        </Grid>
      </div>
    </div>
  );
}
